var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * CZNA-1340 — the "Význam" cell of the semantic catalog's subfield table: what the subfield
 * means in THIS field (`100 $c`), where the wording comes from, and whether NK has changed
 * it from the packaged seed. NK's authority admin edits it here; the record form shows it
 * as the subfield's help.
 *
 * Only the admin sees the edit button (`czna-authorities.subfield-descriptions.item.put`);
 * the backend enforces the same permission, so hiding the button is convenience, not the
 * guard. Clearing the text is a valid save — the row stays, marked as NK's, so the next
 * upgrade does not seed the old text back.
 */
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Modal, ModalFooter, MessageBanner, TextArea, TextField, } from '@folio/stripes/components';
import { useStripes } from '@folio/stripes/core';
import { linkify } from '../components/SubfieldHelpButton';
import { useHelpTextContext } from '../components/HelpTextProvider';
import { subfieldHelpKey } from '../hooks/useHelpTexts';
import useSaveSubfieldDescription from '../hooks/useSaveSubfieldDescription';
import { isTimeoutError } from '../utils/errorKind';
import { badgeStyle } from './semanticCatalogBadges';
var MSG = 'ui-czna-authorities.semanticCatalog.description';
export var SUBFIELD_DESCRIPTION_PERMISSION = 'czna-authorities.subfield-descriptions.item.put';
/** Same limits as the backend (`SubfieldDescriptionServiceImpl`). */
var MAX_DESCRIPTION = 4000;
var MAX_SOURCE = 1000;
var muted = { color: '#666' };
var SubfieldDescriptionEditor = function (_a) {
    var _b, _c;
    var tag = _a.tag, code = _a.code, initialDescription = _a.initialDescription, initialSource = _a.initialSource, onClose = _a.onClose;
    var intl = useIntl();
    var _d = useState(initialDescription), description = _d[0], setDescription = _d[1];
    var _e = useState(initialSource), source = _e[0], setSource = _e[1];
    var save = useSaveSubfieldDescription({ onSuccess: onClose });
    var status = (_c = (_b = save.error) === null || _b === void 0 ? void 0 : _b.response) === null || _c === void 0 ? void 0 : _c.status;
    var tooLong = description.length > MAX_DESCRIPTION || source.length > MAX_SOURCE;
    var footer = (React.createElement(ModalFooter, null,
        React.createElement(Button, { buttonStyle: "primary", disabled: tooLong || save.isLoading, onClick: function () { return save.mutate({ tag: tag, code: code, description: description, source: source }); }, "data-testid": "subfield-description-save" },
            React.createElement(FormattedMessage, { id: "".concat(MSG, ".save") })),
        React.createElement(Button, { onClick: onClose, "data-testid": "subfield-description-cancel" },
            React.createElement(FormattedMessage, { id: "".concat(MSG, ".cancel") }))));
    return (React.createElement(Modal, { open: true, dismissible: true, onClose: onClose, label: intl.formatMessage({ id: "".concat(MSG, ".modalTitle") }, { field: "".concat(tag, " $").concat(code) }), footer: footer },
        save.isError && (React.createElement(MessageBanner, { type: "error" }, isTimeoutError(save.error)
            ? React.createElement(FormattedMessage, { id: "ui-czna-authorities.error.timeout" })
            : React.createElement(FormattedMessage, { id: status === 403 ? "".concat(MSG, ".saveForbidden") : "".concat(MSG, ".saveError") }))),
        React.createElement(TextArea, { name: "subfield-description", label: intl.formatMessage({ id: "".concat(MSG, ".field") }), value: description, onChange: function (e) { return setDescription(e.target.value); }, error: description.length > MAX_DESCRIPTION
                ? intl.formatMessage({ id: "".concat(MSG, ".tooLong") }, { max: MAX_DESCRIPTION })
                : undefined }),
        React.createElement(TextField, { name: "subfield-description-source", label: intl.formatMessage({ id: "".concat(MSG, ".source") }), value: source, onChange: function (e) { return setSource(e.target.value); }, error: source.length > MAX_SOURCE
                ? intl.formatMessage({ id: "".concat(MSG, ".tooLong") }, { max: MAX_SOURCE })
                : undefined })));
};
var SubfieldDescriptionCell = function (_a) {
    var _b, _c;
    var tag = _a.tag, code = _a.code;
    var stripes = useStripes();
    var subfieldDescriptions = useHelpTextContext().subfieldDescriptions;
    var _d = useState(false), editing = _d[0], setEditing = _d[1];
    var help = subfieldDescriptions[subfieldHelpKey(tag, code)];
    var canEdit = stripes.hasPerm(SUBFIELD_DESCRIPTION_PERMISSION);
    var text = (help === null || help === void 0 ? void 0 : help.description.trim()) ? help.description : '';
    return (React.createElement("div", { "data-testid": "subfield-description-".concat(code) },
        text
            ? React.createElement("div", { style: { whiteSpace: 'pre-line' } }, text)
            : React.createElement("span", { style: muted },
                React.createElement(FormattedMessage, { id: "".concat(MSG, ".empty") })),
        (help === null || help === void 0 ? void 0 : help.source) && (React.createElement("div", { style: __assign(__assign({}, muted), { fontSize: '0.85rem', marginTop: '2px' }) },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.subfieldDescription.sourceLabel" }),
            ' ',
            linkify(help.source))),
        React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' } },
            help && (React.createElement("span", { "data-testid": "subfield-description-origin-".concat(code), style: badgeStyle(help.customized ? '#2e7d32' : '#666') },
                React.createElement(FormattedMessage, { id: help.customized ? "".concat(MSG, ".customized") : "".concat(MSG, ".seed") }))),
            canEdit && (React.createElement(Button, { buttonStyle: "link", marginBottom0: true, onClick: function () { return setEditing(true); }, "data-testid": "subfield-description-edit-".concat(code) },
                React.createElement(FormattedMessage, { id: "".concat(MSG, ".edit") })))),
        editing && (React.createElement(SubfieldDescriptionEditor, { tag: tag, code: code, initialDescription: (_b = help === null || help === void 0 ? void 0 : help.description) !== null && _b !== void 0 ? _b : '', initialSource: (_c = help === null || help === void 0 ? void 0 : help.source) !== null && _c !== void 0 ? _c : '', onClose: function () { return setEditing(false); } }))));
};
export default SubfieldDescriptionCell;
